import { Button, Grid, Hidden, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';

import {
  createAgent,
  fetchKnowledgeBaseJournal,
  fetchUser,
  restoreKnowledgeBaseJournalEntry,
  retrieveAgentConnection,
  retrieveAxAgent,
} from '../api';
import { initialAxAgentValues, steps } from '../constants';
import { messages } from '../messages';
import { AxAgentValues, KnowledgeBaseJournalEntry } from '../types';
import { randomAgentName } from '../utils';
import { AIAgentFunctionalities } from './AIAgentFunctionalities';
import { Footer } from './Footer';
import { KnowledgeBase } from './KnowledgeBase';
import { PersonalizeAIAgent } from './PersonalizeAIAgent';
import { useChatProgressorStyles } from './styles';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import useValidation from './useValidation';
import { Widget } from './Widget';
import { VersionHistoryPanel } from './KnowledgeBase/v3/VersionHistoryPanel';

export interface ChatbotProgressorProps {
  accessToken: string;
  accountSlug: string;
  setStartChatbotProgressor: React.Dispatch<React.SetStateAction<boolean>>;
  setAxAgentValues: React.Dispatch<React.SetStateAction<AxAgentValues>>;
  axAgentValues: AxAgentValues;
  initialStep?: 1 | 2 | 3;
  onExit?: () => void;
}

export const ChatbotProgressor: VFC<ChatbotProgressorProps> = ({
  accessToken,
  accountSlug,
  setStartChatbotProgressor,
  setAxAgentValues,
  axAgentValues,
  initialStep,
  onExit,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { external_id: currentUserId, name: currentUserName } = useSelector(makeSelectWhoami());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
    useState<boolean>(false);

  // Version history state — lives here so the panel can replace the widget column
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [journalEntries, setJournalEntries] = useState<KnowledgeBaseJournalEntry[]>([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [activeJournalEntryUuid, setActiveJournalEntryUuid] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreToken, setRestoreToken] = useState(0);
  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const [isCustomWebsiteEnable, setIsCustomWebsiteEnable] = useState(
    (axAgentValues.customWebsites?.length as number) > 0,
  );
  const [currentStep, setCurrentStep] = useState<number>(initialStep ?? 1);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const classes = useChatProgressorStyles({ currentStep });

  const { errors, validateFields, updateErrors } = useValidation();

  useEffect(() => {
    const createInitialAgent = async () => {
      setIsLoading(true);
      try {
        const agentResponse = await createAgent(accountSlug, randomAgentName());

        setAxAgentValues(prev => ({
          ...prev,
          axAgentId: agentResponse.agent_id,
          totalChatAgents: prev.totalChatAgents + 1,
        }));
      } catch (err) {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.agentCreationFailed),
            'error',
          ),
        );
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAgent = async () => {
      setIsLoading(true);
      try {
        const agentData = await retrieveAxAgent(
          accessToken,
          axAgentValues.axAgentId,
        );
        const agentConnectionResponse = await retrieveAgentConnection(
          accountSlug,
          axAgentValues.axAgentId,
        );

        setAxAgentValues(prev => ({
          ...prev,
          // Without it the knowledge base step creates a second, empty one
          // on the first save.
          knowledgeBaseUuid:
            agentData.knowledgeBase?.uuid ?? prev.knowledgeBaseUuid,
          chatbotName: agentData.name,
          emailFallback: agentData.emailFallback,
          phoneNumberFallback: agentData.phoneNumberFallback,
          websiteFallback: agentData.websiteFallback,
          enableInFountainPlatforms:
            agentConnectionResponse.agent_connection
              .enable_in_fountain_platforms,
          smsEnabled:
            agentConnectionResponse.agent_connection.sms_to_chat_agent_enabled,
          brandId: agentConnectionResponse.agent_connection.brand_external_id,
          brands: agentConnectionResponse.agent_connection.available_brands,
          atsConnectionId:
            agentConnectionResponse.agent_connection.ats_connection_id,
          status: agentData.status,
          customWebsites: agentData.allowedOrigins,
          noOfActiveFunnelsVisibleToApplicant:
            agentConnectionResponse.agent_connection
              .no_of_active_funnels_visible_to_applicant,
          applyBotType: agentData.applyBotType,
          isDuplicateSettingCannotApplyAgain:
            agentConnectionResponse.agent_connection
              .is_duplicate_setting_cannot_apply_again,
          careerSiteUrl: agentData.careerSiteUrl,
          languageCode: agentData.languageCode,
          isActiveAgentAvailable:
            agentConnectionResponse.agent_connection.is_active_agent_available,
        }));

        setIsCustomWebsiteEnable(
          (agentData.allowedOrigins?.length as number) > 0,
        );
      } catch (err) {
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.fetchAgentsFailed),
            'error',
          ),
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (axAgentValues.axAgentId) {
      void fetchAgent();
    } else {
      void createInitialAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, axAgentValues.axAgentId]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const resetAgent = () => {
    setStartChatbotProgressor(false);
    setAxAgentValues(initialAxAgentValues);

    setCurrentStep(1);
    onExit?.();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = event.target;
    const newValue = type === 'checkbox' ? checked : value;
    setAxAgentValues(prev => ({
      ...prev,
      [name]: newValue,
    }));

    updateErrors(name, newValue, axAgentValues);
    setHasUnsavedChanges(true);
  };

  const handleDiscard = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedChangesModal(false);
    if (nextUrl) {
      const unblock = history.block(() => undefined);
      history.push(nextUrl);
      unblock();
    } else {
      resetAgent();
    }
  };

  const handleCloseModal = () => {
    setShowUnsavedChangesModal(false);
    setNextUrl(null);
  };

  useEffect(() => {
    const unblock = history.block(location => {
      if (hasUnsavedChanges) {
        setNextUrl(location.pathname + location.search + location.hash);
        setShowUnsavedChangesModal(true);
        return false; // Block navigation
      }
      return undefined; // Explicitly return undefined to allow navigation
    });

    return () => {
      unblock();
    };
  }, [hasUnsavedChanges, history]);

  const handleGoBackToAgentList = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      resetAgent();
    }
  };

  // Resolves external_ids to display names for journal entries.
  // Current user is resolved from whoami (no API call); others use fetchUser.
  const resolveActorNames = useCallback(
    async (entries: KnowledgeBaseJournalEntry[]): Promise<Record<string, string>> => {
      const uniqueIds = [...new Set(entries.map(e => e.createdBy).filter(Boolean))] as string[];
      const resolved: Record<string, string> = {};
      await Promise.allSettled(
        uniqueIds.map(async id => {
          if (id === currentUserId) {
            resolved[id] = currentUserName ?? 'You';
          } else {
            try {
              const { user } = await fetchUser(accountSlug, id);
              resolved[id] = user.name;
            } catch {
              resolved[id] = id; // fallback to raw id on failure
            }
          }
        }),
      );
      return resolved;
    },
    [accountSlug, currentUserId, currentUserName],
  );

  const handleViewVersionHistory = useCallback(async () => {
    if (showVersionHistory) {
      setShowVersionHistory(false);
      return;
    }

    setShowVersionHistory(true);

    const kbUuid = axAgentValues.knowledgeBaseUuid;
    if (!kbUuid) return;

    try {
      setJournalLoading(true);
      const { entries } = await fetchKnowledgeBaseJournal(accessToken, kbUuid);
      const names = await resolveActorNames(entries);
      setJournalEntries(entries);
      setActorNames(names);
      setActiveJournalEntryUuid(entries[0]?.uuid ?? null);
    } catch {
      // silently ignore — panel stays open with empty state
    } finally {
      setJournalLoading(false);
    }
  }, [accessToken, axAgentValues.knowledgeBaseUuid, resolveActorNames, showVersionHistory]);

  const handleRestore = useCallback(async (journalUuid: string) => {
    const kbUuid = axAgentValues.knowledgeBaseUuid;
    if (!kbUuid) return;
    try {
      setIsRestoring(true);
      await restoreKnowledgeBaseJournalEntry(accessToken, kbUuid, journalUuid, currentUserId ?? undefined);
      // Refresh journal entries and re-resolve names
      const { entries } = await fetchKnowledgeBaseJournal(accessToken, kbUuid);
      const names = await resolveActorNames(entries);
      setJournalEntries(entries);
      setActorNames(names);
      setActiveJournalEntryUuid(entries[0]?.uuid ?? null);
      setRestoreToken(prev => prev + 1);
    } catch {
      dispatch(
        addMessageAction(
          intl.formatMessage(messages.restoreFailed),
          'error',
        ),
      );
    } finally {
      setIsRestoring(false);
    }
  }, [accessToken, axAgentValues.knowledgeBaseUuid, currentUserId, dispatch, intl, resolveActorNames]);

  // Silently refresh journal (no loading flash) when a FAQ changes while panel is open.
  const handleFaqChange = useCallback(async () => {
    if (!showVersionHistory) return;
    const kbUuid = axAgentValues.knowledgeBaseUuid;
    if (!kbUuid) return;
    try {
      const { entries } = await fetchKnowledgeBaseJournal(accessToken, kbUuid);
      const names = await resolveActorNames(entries);
      setJournalEntries(entries);
      setActorNames(names);
      setActiveJournalEntryUuid(entries[0]?.uuid ?? null);
    } catch {
      // silently ignore
    }
  }, [accessToken, axAgentValues.knowledgeBaseUuid, resolveActorNames, showVersionHistory]);

  // Reset version history when leaving step 3
  useEffect(() => {
    if (currentStep !== 3) {
      setShowVersionHistory(false);
      setJournalEntries([]);
    }
  }, [currentStep]);

  const renderStep = (): JSX.Element => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalizeAIAgent
            axAgentValues={axAgentValues}
            accessToken={accessToken}
            setAxAgentValues={setAxAgentValues}
            setHasUnsavedChanges={setHasUnsavedChanges}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            accountSlug={accountSlug}
            handleChange={handleChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <>
            <AIAgentFunctionalities
              handleChange={handleChange}
              setAxAgentValues={setAxAgentValues}
              axAgentValues={axAgentValues}
              errors={errors}
              isLoading={isLoading}
              isCustomWebsiteEnable={isCustomWebsiteEnable}
              setIsCustomWebsiteEnable={setIsCustomWebsiteEnable}
            />
          </>
        );
      case 3:
        return (
          <>
            <KnowledgeBase
              setAxAgentValues={setAxAgentValues}
              accessToken={accessToken}
              axAgentValues={axAgentValues}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
              onViewVersionHistory={() => void handleViewVersionHistory()}
              showVersionHistory={showVersionHistory}
              restoreToken={restoreToken}
              onFaqChange={() => void handleFaqChange()}
            />
          </>
        );
      default:
        return <div>Step not found</div>;
    }
  };

  return (
    <div className={classes.chatbotProgressor}>
      {/* Left column: header + scrollable step content + footer */}
      <Grid className={classes.contentContainer}>
        <div className={classes.progressorHeader}>
          <div className={classes.backToAgentList}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                handleGoBackToAgentList();
              }}
            >
              Back to Candidate AI Agent
            </Button>
          </div>
          <Hidden smDown>
            <div className={classes.stepProgressBar}>
              {steps.map(step => (
                <div key={step.number} className={classes.stepContainer}>
                  <div
                    className={`${classes.step} ${
                      step.number <= currentStep
                        ? classes.active
                        : classes.inactive
                    }`}
                  >
                    {step.number < currentStep ? '✓' : step.number}
                  </div>
                  <Typography variant="caption">{step.label}</Typography>
                </div>
              ))}
            </div>
          </Hidden>
        </div>
        <Grid className={classes.stepContent} ref={containerRef}>{renderStep()}</Grid>
        {!isLoading && (
          <Footer
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            setAxAgentValues={setAxAgentValues}
            axAgentValues={axAgentValues}
            accessToken={accessToken}
            setStartChatbotProgressor={setStartChatbotProgressor}
            validateFields={() => validateFields(axAgentValues, currentStep)}
            resetAgent={resetAgent}
            accountSlug={accountSlug}
            setHasUnsavedChanges={setHasUnsavedChanges}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        )}
      </Grid>
      {/* Right column: widget preview / version history */}
      <Grid className={classes.widgetContainer}>
        <div className={classes.widgetSlideWrapper}>
          {/* Tray holds widget + panel side-by-side; slides left to reveal panel */}
          <div
            className={`${classes.widgetSlideTray}${
              showVersionHistory ? ` ${classes.widgetSlideTrayShifted}` : ''
            }`}
          >
            <div className={classes.widgetSlideItem}>
              {!isLoading && <Widget axAgentId={axAgentValues.axAgentId} />}
            </div>
            <div className={classes.widgetSlideItem}>
              <VersionHistoryPanel
                entries={journalEntries}
                isLoading={journalLoading}
                activeEntryUuid={activeJournalEntryUuid}
                onEntryClick={setActiveJournalEntryUuid}
                onClose={() => setShowVersionHistory(false)}
                onRestore={handleRestore}
                isRestoring={isRestoring}
                actorNames={actorNames}
              />
            </div>
          </div>
        </div>
      </Grid>
      {showUnsavedChangesModal && (
        <UnsavedChangesModal
          handleClose={handleCloseModal}
          handleDiscard={handleDiscard}
          open
        />
      )}
    </div>
  );
};

export default ChatbotProgressor;
